// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartTileBackSideFooterConfig.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import routeUtils from 'share/utils/Izenda.RouteUtils';
import BrowserUtils from 'share/utils/Izenda.BrowserUtils';
import localizer from 'share/localization/Izenda.Localizer';
import exportUtils from 'share/utils/Izenda.ExportUtils';
import LicenseCheckerConstants from 'logic/data/Izenda.LicenseCheckerConstants';
import AuthorizationConstants from 'logic/data/Izenda.AuthorizationConstants';
import { uniqueId } from 'lodash/fp';
export default function(reportId, reportPartId, props) {
  const viewUrl = routeUtils.getReportUrl('View', {
    id: reportId
  });
  const editUrl = routeUtils.getReportUrl('Edit', {
    id: reportId
  });

  const viewReportDesignerUrl = routeUtils.getReportDesignerUrl('ReportDesignerDetail', {
    reportId
  });

  const handlePrint = () => {
    const url = routeUtils.getViewerUrl('ReportBody', {
      id: reportId
    });
    const filterInfo = props.dashboardPart.filterInfoForReport;

    const queryString = $izenda.param(filterInfo || {});
    BrowserUtils.openNewTab(routeUtils.makeAbsoluteUrl(url, `print=true&${queryString}`));
  };

  const authorization = props.authorization;
  const permissionKeys = props.authorizationConstants.PERMISSION_KEY;

  const exportReportPart = format => {
    const name = props.dashboardPart.reportPart.headerTitle;
    const exportLoader = props.exportLoader;
    const key = uniqueId(name);

    exportLoader.startExportLoading({
      name: String.format('{0}.{1}', name, exportUtils.getFileTypeByFormat(format)),
      key,
      error: false
    });

    const url = window.location.href;
    const filters = props.dashboardPart.filters;

    return props.store.exportReportPart(
      url,
      format,
      reportPartId,
      reportId,
      filters,
      props.dashboardPart.id,
      exportLoader.endExportLoading.bind(exportLoader, key),
      exportLoader.updateError.bind(exportLoader, key)
    );
  };

  return [
    {
      key: 'open',
      get label() {
        return localizer.getText('REPORT_PART_TILE_BACK_SIDE_FOOTER_CONFIG_OPEN');
      },
      link: viewUrl
    },
    {
      key: 'edit',
      get label() {
        return localizer.getText('REPORT_PART_TILE_BACK_SIDE_FOOTER_CONFIG_EDIT');
      },
      state: { disabled: false, hide: false },
      btnGroup: [
        {
          key: 'quickEdit',
          get label() {
            return localizer.getText('REPORT_PART_TILE_BACK_SIDE_FOOTER_CONFIG_QUICK_EDIT');
          },
          state: { disabled: false, hide: false },
          link: editUrl,
          hasPrivilege: authorization.checkPrivileges(
            [permissionKeys.ReportsCanCreateNewReportValue, permissionKeys.ReportsActionsOverwriteExistingReport],
            props.authorizationConstants.PERMISSION_OPERATOR.Or
          ),
          accessPriority: [
            AuthorizationConstants.ACCESS_RIGHT.FullAccess,
            AuthorizationConstants.ACCESS_RIGHT.QuickEdit,
            AuthorizationConstants.ACCESS_RIGHT.SaveAs
          ]
        },
        {
          key: 'design',
          get label() {
            return localizer.getText('REPORT_PART_TILE_BACK_SIDE_FOOTER_CONFIG_DESIGN');
          },
          state: { disabled: false, hide: false },
          link: viewReportDesignerUrl,
          hasPrivilege: authorization.checkPrivileges(
            [permissionKeys.ReportsActionsOverwriteExistingReport, permissionKeys.ReportsCanCreateNewReportValue],
            props.authorizationConstants.PERMISSION_OPERATOR.Or
          ),
          accessPriority: [AuthorizationConstants.ACCESS_RIGHT.FullAccess, AuthorizationConstants.ACCESS_RIGHT.SaveAs]
        }
      ]
    },
    {
      key: 'html',
      get label() {
        return localizer.getText('COMMON_LABEL_PRINT');
      },
      state: { disabled: false, hide: false },
      hasLicense() {
        return authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.Exporting);
      },
      onClick: handlePrint,
      hasPrivilege() {
        return authorization.checkPrivileges([permissionKeys.ReportsActionsPrint]);
      }
    },
    {
      key: 'export',
      get label() {
        return localizer.getText('COMMON_LABEL_EXPORT');
      },
      state: { disabled: false, hide: false },
      hasLicense: authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.Exporting),
      btnGroup: [
        {
          key: 'word',
          label: 'Word',
          state: {
            disabled: false,
            hide: false
          },
          props: {
            onClick: exportReportPart.bind(this, 'word')
          },
          hasPrivilege: authorization.checkPrivileges([permissionKeys.ExportingExportingFormatWord])
        },
        {
          key: 'excel',
          label: 'Excel',
          state: {
            disabled: false,
            hide: false
          },
          props: {
            onClick: exportReportPart.bind(this, 'excel')
          },
          hasPrivilege: authorization.checkPrivileges([permissionKeys.ExportingExportingFormatExcel])
        },
        {
          key: 'pdf',
          label: 'PDF',
          state: {
            disabled: false,
            hide: false
          },
          props: {
            onClick: exportReportPart.bind(this, 'pdf')
          },
          hasPrivilege: authorization.checkPrivileges(
            [permissionKeys.ExportingExportingFormatPdf],
            props.authorizationConstants.PERMISSION_OPERATOR.Or
          )
        },
        {
          key: 'csv',
          label: 'CSV',
          state: {
            disabled: false,
            hide: false
          },
          props: {
            onClick: exportReportPart.bind(this, 'csv')
          },
          hasPrivilege: authorization.checkPrivileges(
            [permissionKeys.ExportingExportingFormatCsv],
            props.authorizationConstants.PERMISSION_OPERATOR.Or
          )
        },
        {
          key: 'xml',
          label: 'XML',
          state: {
            disabled: false,
            hide: false
          },
          props: {
            onClick: exportReportPart.bind(this, 'xml')
          },
          hasPrivilege: authorization.checkPrivileges(
            [permissionKeys.ExportingExportingFormatXml],
            props.authorizationConstants.PERMISSION_OPERATOR.Or
          )
        },
        {
          key: 'json',
          label: 'JSON',
          state: {
            disabled: false,
            hide: false
          },
          props: {
            onClick: exportReportPart.bind(this, 'json')
          },
          hasPrivilege: authorization.checkPrivileges(
            [permissionKeys.ExportingExportingFormatJson],
            props.authorizationConstants.PERMISSION_OPERATOR.Or
          )
        },
        {
          key: 'query',
          label: 'Query Execution',
          state: {
            disabled: false,
            hide: false
          },
          props: {
            onClick: exportReportPart.bind(this, 'query')
          },
          hasPrivilege: authorization.checkPrivileges(
            [permissionKeys.ExportingExportingFormatQueryExecution],
            props.authorizationConstants.PERMISSION_OPERATOR.Or
          )
        }
      ],
      hasPrivilege() {
        return authorization.checkPrivileges(
          [permissionKeys.ReportsActionsExporting],
          props.authorizationConstants.PERMISSION_OPERATOR.Or
        );
      }
    },
    {
      key: 'selectReportPart',
      get label() {
        return localizer.getText('REPORT_PART_TILE_BACK_SIDE_FOOTER_CONFIG_SELECT_REPORT_PART');
      },
      onClick: props.onSelectReportPartButton,
      hasPrivilege() {
        return authorization.checkPrivileges(
          [
            permissionKeys.DashboardsActionsOverwriteExistingDashboard,
            permissionKeys.DashboardsCanCreateNewDashboardValue
          ],
          props.authorizationConstants.PERMISSION_OPERATOR.Or
        );
      }
    }
  ];
}



// WEBPACK FOOTER //
// ./ui/logic/data/Izenda.ReportPartTileBackSideFooterConfig.js